import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tenantService } from '../../services/tenantService';

interface TenantState {
  tenants: Tenant[];
  currentTenant: Tenant | null;
  loading: boolean;
  error: string | null;
}

interface Tenant {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'inactive';
  createdAt: string;
  updatedAt: string;
  settings: Record<string, any>;
}

const initialState: TenantState = {
  tenants: [],
  currentTenant: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchTenants = createAsyncThunk(
  'tenant/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await tenantService.getAllTenants();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch tenants');
    }
  }
);

export const setCurrentTenant = createAsyncThunk(
  'tenant/setCurrent',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const tenant = await tenantService.getTenantById(tenantId);
      localStorage.setItem('currentTenantId', tenantId);
      return tenant;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to set tenant');
    }
  }
);

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tenants
    builder.addCase(fetchTenants.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTenants.fulfilled, (state, action) => {
      state.tenants = action.payload;
      state.loading = false;

      // Set current tenant if not set
      if (!state.currentTenant && action.payload.length > 0) {
        state.currentTenant = action.payload[0];
      }
    });
    builder.addCase(fetchTenants.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Set current tenant
    builder.addCase(setCurrentTenant.fulfilled, (state, action) => {
      state.currentTenant = action.payload;
    });
  },
});

export const { clearError } = tenantSlice.actions;
export default tenantSlice.reducer;
